/**
 * @file MUI Icon mapping
 */

import AccountCircle from '@mui/icons-material/AccountCircleOutlined';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import Apartment from '@mui/icons-material/ApartmentRounded';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import ArrowDropUpRoundedIcon from '@mui/icons-material/ArrowDropUpRounded';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import AttachFile from '@mui/icons-material/AttachFileRounded';
import AutoAwesome from '@mui/icons-material/AutoAwesomeOutlined';
import BackspaceOutlinedIcon from '@mui/icons-material/BackspaceOutlined';
import ChatBubbleOutline from '@mui/icons-material/ChatBubbleOutlineRounded';
import CheckCircle from '@mui/icons-material/CheckCircleOutlineRounded';
import Check from '@mui/icons-material/CheckRounded';
import ChecklistRoundedIcon from '@mui/icons-material/ChecklistRounded';
import ChevronLeft from '@mui/icons-material/ChevronLeftRounded';
import ChevronRight from '@mui/icons-material/ChevronRightRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ContentPasteOutlined from '@mui/icons-material/ContentPasteOutlined';
import Copyright from '@mui/icons-material/CopyrightRounded';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import DonutLargeRoundedIcon from '@mui/icons-material/DonutLargeRounded';
import EastRoundedIcon from '@mui/icons-material/EastRounded';
import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import FilterList from '@mui/icons-material/FilterListRounded';
import FormatBoldRoundedIcon from '@mui/icons-material/FormatBoldRounded';
import FormatItalicRoundedIcon from '@mui/icons-material/FormatItalicRounded';
import FormatListBulletedRoundedIcon from '@mui/icons-material/FormatListBulletedRounded';
import FormatListNumberedRoundedIcon from '@mui/icons-material/FormatListNumberedRounded';
import FullscreenRoundedIcon from '@mui/icons-material/FullscreenRounded';
import GppMaybeOutlined from '@mui/icons-material/GppMaybeOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import GroupsOutlined from '@mui/icons-material/GroupsOutlined';
import HelpOutline from '@mui/icons-material/HelpOutlineRounded';
import LabelOutlined from '@mui/icons-material/LabelOutlined';
import LocalHotelOutlined from '@mui/icons-material/LocalHotelOutlined';
import LockOpen from '@mui/icons-material/LockOpenRounded';
import LockOutlined from '@mui/icons-material/LockOutlined';
import Logout from '@mui/icons-material/LogoutRounded';
import MaximizeRoundedIcon from '@mui/icons-material/MaximizeRounded';
import MessageOutlined from '@mui/icons-material/MessageOutlined';
import MinimizeRoundedIcon from '@mui/icons-material/MinimizeRounded';
import ModeEditOutlineOutlined from '@mui/icons-material/ModeEditOutlineOutlined';
import MoreVert from '@mui/icons-material/MoreVert';
import NotificationsNoneRounded from '@mui/icons-material/NotificationsNoneRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import PersonOutline from '@mui/icons-material/PersonOutlineRounded';
import PlaylistAddCheckRoundedIcon from '@mui/icons-material/PlaylistAddCheckRounded';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import ReorderRoundedIcon from '@mui/icons-material/ReorderRounded';
import Search from '@mui/icons-material/SearchRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import SettingsSuggest from '@mui/icons-material/SettingsSuggestRounded';
import Speed from '@mui/icons-material/SpeedRounded';
import StrikethroughSRoundedIcon from '@mui/icons-material/StrikethroughSRounded';
import SwapVerticalCircleOutlined from '@mui/icons-material/SwapVerticalCircleOutlined';
import TimerIcon from '@mui/icons-material/Timer';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import Topic from '@mui/icons-material/TopicOutlined';
import Translate from '@mui/icons-material/TranslateRounded';
import Tune from '@mui/icons-material/TuneRounded';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import WestRounded from '@mui/icons-material/WestRounded';

import { FC } from 'react';

import MaterialSymbolCalendarIcon from './MaterialSymbolIcon/Calendar';
import MaterialSymbolClockIcon from './MaterialSymbolIcon/Clock';
import MaterialSymbolClockAltIcon from './MaterialSymbolIcon/ClockAlt';
import MaterialSymbolClockFilledIcon from './MaterialSymbolIcon/ClockFilled';
import MaterialSymbolDownloadIcon from './MaterialSymbolIcon/Download';
import MaterialSymbolErrorIcon from './MaterialSymbolIcon/Error';
import MaterialSymbolFolderNewIcon from './MaterialSymbolIcon/FolderNew';
import MaterialSymbolHomeIcon from './MaterialSymbolIcon/Home';
import MaterialSymbolInfoIcon from './MaterialSymbolIcon/Info';
import MaterialSymbolRemoveIcon from './MaterialSymbolIcon/Remove';
import MaterialSymbolSettingsIcon from './MaterialSymbolIcon/Settings';
import MaterialSymbolWarningIcon from './MaterialSymbolIcon/Warning';
import { IconPropsExtended, MuiIconName, MuiSymbolIconName } from './types';

export const muiIconMappings: Record<MuiIconName, typeof AccountCircle> = {
  add: AddRoundedIcon,
  apartment: Apartment,
  archive: ArchiveOutlinedIcon,
  arrowDropDown: ArrowDropDownRoundedIcon,
  arrowDropUp: ArrowDropUpRoundedIcon,
  arrowLeft: WestRounded,
  arrowRight: EastRoundedIcon,
  assignment: AssignmentOutlinedIcon,
  attachFile: AttachFile,
  back: ArrowBackRoundedIcon,
  bold: FormatBoldRoundedIcon,
  bulletList: FormatListBulletedRoundedIcon,
  changeObject: SwapVerticalCircleOutlined,
  chatBubble: ChatBubbleOutline,
  chatbotLulu: AutoAwesome,
  check: Check,
  checkCircle: CheckCircle,
  checklist: ChecklistRoundedIcon,
  chevronLeft: ChevronLeft,
  chevronRight: ChevronRight,
  clear: BackspaceOutlinedIcon,
  close: CloseRoundedIcon,
  copyright: Copyright,
  createOutlined: CreateOutlinedIcon,
  deleteOutlined: DeleteOutlineRoundedIcon,
  devPanel: SettingsSuggest,
  drag: ReorderRoundedIcon,
  dueDate: EventAvailableOutlinedIcon,
  edit: ModeEditOutlineOutlined,
  expandLess: ExpandLessRoundedIcon,
  expandMore: ExpandMoreRoundedIcon,
  filter: FilterList,
  filterOutlined: FilterAltOutlinedIcon,
  formatList: FormatListNumberedRoundedIcon,
  fullscreen: FullscreenRoundedIcon,
  help: HelpOutline,
  hotel: LocalHotelOutlined,
  italic: FormatItalicRoundedIcon,
  label: LabelOutlined,
  lock: LockOutlined,
  lockOpen: LockOpen,
  logout: Logout,
  maximize: MaximizeRoundedIcon,
  minimize: MinimizeRoundedIcon,
  more: MoreVert,
  notification: NotificationsNoneRounded,
  openInNew: OpenInNewRoundedIcon,
  post: EditNoteRoundedIcon,
  preview: VisibilityOutlinedIcon,
  privacy: GppMaybeOutlined,
  profile: AccountCircle,
  refresh: RefreshRounded,
  replies: MessageOutlined,
  search: Search,
  send: SendRoundedIcon,
  speed: Speed,
  status: DonutLargeRoundedIcon,
  strikethrough: StrikethroughSRoundedIcon,
  task: PlaylistAddCheckRoundedIcon,
  template: ContentPasteOutlined,
  timer: TimerIcon,
  timerOutlined: TimerOutlinedIcon,
  topic: Topic,
  translate: Translate,
  tune: Tune,
  unarchive: UnarchiveOutlinedIcon,
  user: PersonOutline,
  userGroup: GroupsOutlined,
  users: GroupOutlinedIcon,
};

export const mappingMaterialSymbolIcon: Record<
  MuiSymbolIconName,
  FC<IconPropsExtended>
> = {
  calendar: MaterialSymbolCalendarIcon,
  clock: MaterialSymbolClockIcon,
  clockAlt: MaterialSymbolClockAltIcon,
  clockFilled: MaterialSymbolClockFilledIcon,
  download: MaterialSymbolDownloadIcon,
  error: MaterialSymbolErrorIcon,
  folderNew: MaterialSymbolFolderNewIcon,
  home: MaterialSymbolHomeIcon,
  info: MaterialSymbolInfoIcon,
  remove: MaterialSymbolRemoveIcon,
  settings: MaterialSymbolSettingsIcon,
  warning: MaterialSymbolWarningIcon,
};

export type IconName = MuiIconName | MuiSymbolIconName;
